import { createTheme } from "@material-ui/core/styles";

export const colors = {
  // Base
  softBlack: "#231F20",
  black: "#000",
  white: "#fff",
  accentPurple: "#8e3ca4",

  charcoal: {
    soft: "#8f93b3",
    light: "#dddfe8",
  },

  // Turbine Red
  red: {
    0: "#f9dde3",
    50: "#f6cdd6",
    100: "#f1acbb",
    200: "#ec8ca0",
    300: "#e76b85",
    400: "#e24b6a",
    500: "#da1a42",
    600: "#c4173b",
    700: "#ae1535",
    800: "#99122e",
    900: "#831028",
  },

  // Ignite Orange
  orange: {
    0: "#fff1ec",
    50: "#ffe9e0",
    100: "#ffdacc",
    200: "#ffc2ad",
    300: "#ff9a73",
    400: "#ff6e3d",
    500: "#f04c0a",
    600: "#d03a08",
    700: "#af2807",
    800: "#8f1505",
    900: "#6e0303",
  },

  // Pink
  pink: {
    0: "#fef2fb",
    50: "#feebf9",
    100: "#fddff5",
    200: "#fcbfea",
    300: "#f78ed8",
    400: "#e64fb8",
    500: "#cc0e93",
    600: "#b00c7f",
    700: "#930a6a",
    800: "#770756",
    900: "#5a0541",
  },

  // Monetization Purple
  purple: {
    0: "#f7f2fc",
    50: "#f2eafb",
    100: "#e7d9f7",
    200: "#d3b8f1",
    300: "#b688e7",
    400: "#9c5ede",
    500: "#8336d6",
    600: "#732dc0",
    700: "#6324aa",
    800: "#521d8d",
    900: "#421772",
  },

  // Golden Yellow
  yellow: {
    0: "#fff6e5",
    50: "#fff0d4",
    100: "#ffe9be",
    200: "#ffdfa3",
    300: "#ffd079",
    400: "#ffb733",
    500: "#ffa500",
    600: "#e68e00",
    700: "#cc7000",
    800: "#a85100",
    900: "#803a00",
  },

  // Grass Blade
  green: {
    0: "#ebf3e1",
    50: "#e0ecd1",
    100: "#d3e4be",
    200: "#c6dcaa",
    300: "#b9d597",
    400: "#abcc82",
    500: "#99c167",
    600: "#5e7f34",
    700: "#55722f",
    800: "#425924",
    900: "#384c1f",
  },

  // Secondary
  // Dark Blue
  plum: {
    0: "#fbf1fb",
    50: "#f8e8f8",
    100: "#f2d5f2",
    200: "#e1a6e1",
    300: "#cb79cf",
    400: "#a442a9",
    500: "#882e8d",
    600: "#732277",
    700: "#5f1862",
    800: "#571264",
    900: "#4f0b65", // Plum Dark
  },

  // Grays
  gray: {
    0: "#fafafa",
    50: "#f5f5f5",
    100: "#f0f0f0",
    200: "#eaeaea",
    300: "#e1e1e1",
    400: "#cacaca",
    500: "#b3b3b3",
    600: "#8e8e8e",
    700: "#676767",
    800: "#414141",
    900: "#1a1a1a",
  },
};

const theme = createTheme({
  palette: {
    background: { default: colors.gray[100] },
    primary: {
      light: colors.purple[50],
      main: colors.purple[500],
      dark: colors.purple[900],
      contrastText: colors.white,
    },
    secondary: {
      light: colors.plum[100],
      main: colors.plum[500],
      dark: colors.plum[900],
      contrastText: colors.white,
    },
    success: { main: colors.green[500] },
    warning: { main: colors.yellow[500] },
    error: { main: colors.red[500] },
  },

  typography: {
    fontFamily: "Inter, sans-serif",
  },

  props: {
    MuiCheckbox: {
      color: "primary",
    },
  },

  overrides: {
    MuiButton: {
      root: {
        fontWeight: 600,
        textTransform: "capitalize",
        padding: "8px 16px",
        letterSpacing: "1.25px",
        minHeight: 36,
      },
      sizeSmall: {
        minHeight: 24,
        padding: "4px 16px",
        fontSize: 12,
        lineHeight: "16px",
      },
    },
    MuiChip: {
      root: {
        backgroundColor: "#E8E8E8",
        fontWeight: 400,
        height: 26,
      },
    },
    MuiAccordion: {
      rounded: {
        borderRadius: 5,
      },
      root: {
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.02)",
      },
    },
    MuiAccordionSummary: {
      content: {
        color: colors.softBlack,
        fontWeight: 400,
        fontSize: 16,
      },
      root: {
        "&.Mui-expanded": {
          borderBottom: `1px solid ${colors.gray[100]}`,
        },
      },
    },
    MuiFormControl: {
      root: {
        marginTop: 8,
        marginBottom: 4,
      },
    },
    MuiFormControlLabel: {
      root: {
        height: 32,
        overflowY: "visible",
      },
      label: {
        fontSize: "14px",
        color: colors.softBlack,
      },
    },
    MuiFormHelperText: {
      root: {
        fontWeight: 300,
        minHeight: 19.2,
      },
    },
    MuiFormLabel: {
      root: {
        fontSize: 14,
        color: colors.softBlack,
        "&$focused": {
          color: colors.purple[700],
        },
        "&$disabled": {
          color: colors.softBlack,
        },
      },
    },
    MuiSwitch: {
      colorPrimary: {
        "&$checked": {
          color: colors.purple[500],
        },
        "&$checked + $track": {
          backgroundColor: colors.purple[50],
          opacity: 1,
        },
      },
    },
    MuiCheckbox: {
      colorPrimary: {
        "&$checked": { color: colors.purple[500] },
      },
    },
    MuiInput: {
      input: {
        fontSize: 14,
        height: 19,
        paddingTop: 3,
        "&$disabled": {
          opacity: 0.5,
          color: colors.softBlack,
        },
      },
      inputMultiline: {
        height: "auto",
      },
      underline: {
        "&:before": {
          borderBottomColor: colors.gray[300],
        },
        "&:after": {
          borderBottomColor: colors.gray[300],
        },
        "&$focused:after": {
          borderBottomColor: colors.purple[700],
        },
        "&:hover:not($disabled):not($focused):not($error):before": {
          borderBottomColor: colors.gray[300],
        },
        "&$error:after": {
          borderBottomColor: colors.red[500],
        },
        "&$error:hover:not($disabled):not($focused):after": {
          borderBottomColor: colors.red[500],
        },
        "&:hover:not($disabled):before": {
          borderBottomColor: colors.gray[300],
        },
      },
    },
    MuiInputBase: {
      root: {
        marginTop: 0,
        fontWeight: 400,
      },
    },
    MuiInputAdornment: {
      positionStart: {
        marginRight: 0,
      },
    },
    MuiInputLabel: {
      shrink: {
        fontSize: 16,
      },
    },
    MuiLink: {
      root: {
        cursor: "pointer",
      },
    },
    MuiListItemIcon: {
      root: {
        color: colors.white,
      },
    },
    MuiListItemText: {
      primary: {
        // fontWeight: 400,
      },
    },
    MuiMenuItem: {
      root: {
        fontSize: 14,
      },
      dense: {
        fontWeight: 400,
      },
    },
    MuiOutlinedInput: {
      root: {
        "&.Mui-error $notchedOutline": {
          borderWidth: 2,
        },
        "&:hover $notchedOutline": {
          borderColor: colors.purple[600],
          borderWidth: 2,
        },
      },
    },
    MuiSelect: {
      selectMenu: {
        height: 19,
      },
    },
    MuiSlider: {
      root: {
        height: 5,
      },
      markLabel: {
        fontSize: 12,
      },
      rail: {
        color: colors.gray[700],
        height: 5,
        borderRadius: 3,
      },
      thumb: {
        height: 14,
        width: 14,
        "&:focus,&:hover,&$active": {
          boxShadow: "inherit",
        },
      },
      track: {
        height: 5,
        borderRadius: 3,
      },
    },
    MuiSvgIcon: {
      colorPrimary: {
        color: colors.white,
      },
    },
    MuiTable: { root: { tableLayout: "fixed", width: "100%" } },
    MuiTableCell: {
      root: { wordBreak: "break-word", borderBottomWidth: 0.5 },
      body: {
        fontWeight: 400,
        padding: 12,

        "&:not($paddingNone):first-child": {
          paddingLeft: 24,
        },
        "&:last-child": {
          paddingRight: 24,
        },
      },
      head: {
        color: colors.softBlack,
        fontWeight: 600,
        paddingLeft: 12,
        paddingRight: 12,

        "&:not($paddingNone):first-child": {
          paddingLeft: 24,
        },
        "&:last-child": {
          paddingRight: 24,
        },
      },
      sizeSmall: { paddingLeft: 8, paddingRight: 8 },
      stickyHeader: {
        backgroundColor: "white",
      },
    },
    MuiTableRow: {
      root: {
        "&[disabled]": {
          "& > td": {
            opacity: 0.5,
            backgroundColor: colors.gray[100],
          },
        },
        "&:not([nohover]):hover > td": {
          backgroundColor: "rgba(245, 245, 245) !important",
        },
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: colors.white,
        color: colors.softBlack,
      },
    },
    MuiTypography: {
      h3: {
        fontSize: "20px",
        fontWeight: 300,
      },
      h4: {
        fontSize: "18px",
        fontWeight: 300,
        lineHeight: "normal",
      },
      h5: {
        fontSize: "16px",
        fontWeight: 400,
      },
      h6: {
        fontSize: "14px",
        fontWeight: 300,
      },
    },
  },
});

export default theme;
